/*
  ML-4448 — Urn Size Calculator

  - Filters products rendered in the section by:
    - data-urn-type="human" | "pet"
    - data-capacity="<number>" (pounds)
  - Converts kg -> lb for matching
  - Shows 9 products per page with Prev/Next controls
  - If the chosen collection has more than 250 products, the section is rendered in pages.
    This script can fetch additional section pages on-demand via Shopify's section rendering endpoint.
*/

(function () {
  "use strict";

  var LB_PER_KG = 2.2046226218;

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function toNumber(value) {
    if (value === null || value === undefined) return NaN;
    var cleaned = String(value).replace(/[^0-9.+-]/g, "");
    var n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }

  function formatNumber(n, decimals) {
    if (!Number.isFinite(n)) return "—";
    var d = typeof decimals === "number" ? decimals : 0;
    return n.toFixed(d);
  }

  function formatSmart(n) {
    if (!Number.isFinite(n)) return "—";
    // Round to 1 decimal, but omit trailing .0
    var rounded = Math.round(n * 10) / 10;
    if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
    return rounded.toFixed(1);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      window.clearTimeout(t);
      t = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function preferredScrollBehavior() {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return "auto";
      }
    } catch (e) {
      // Ignore and fall back to smooth scrolling.
    }
    return "smooth";
  }

  function ML4448(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.getAttribute("data-ml4448-section-id") || "";

    this.perPage = toNumber(sectionEl.getAttribute("data-per-page")) || 9;
    this.subject = null;
    this.unit = "lb";
    this.currentPage = 1;

    this.weightInput = sectionEl.querySelector("[data-ml4448-weight]");
    this.subjectBtns = Array.prototype.slice.call(sectionEl.querySelectorAll("[data-ml4448-subject]"));
    this.unitBtns = Array.prototype.slice.call(sectionEl.querySelectorAll("[data-ml4448-unit]"));


    // Read initial toggle states from markup (if any are pre-selected).
    var pressedSubjectBtn = sectionEl.querySelector('[data-ml4448-subject][aria-pressed="true"]');
    if (pressedSubjectBtn) {
      this.subject = pressedSubjectBtn.getAttribute("data-ml4448-subject");
    }

    var pressedUnitBtn = sectionEl.querySelector('[data-ml4448-unit][aria-pressed="true"]');
    if (pressedUnitBtn) {
      this.unit = pressedUnitBtn.getAttribute("data-ml4448-unit") || this.unit;
    }

    // Subject selection validation UI
    this.subjectErrorEl = sectionEl.querySelector("[data-ml4448-subject-error]");
    this.subjectSegEl = this.subjectBtns.length ? this.subjectBtns[0].closest(".ml4448__seg") : null;

    this.recoEl = sectionEl.querySelector("[data-ml4448-recommendation]");
    this.weightDisplayEl = sectionEl.querySelector("[data-ml4448-weight-display]");
    this.capacityDisplayEl = sectionEl.querySelector("[data-ml4448-cap-display]");
    this.totalCountEl = sectionEl.querySelector("[data-ml4448-total-count]");
    this.enteredWeightUnitEl = sectionEl.querySelector("[data-ml4448-entered-weight-unit]");
    this.subjectDisplayEl = sectionEl.querySelector("[data-ml4448-subject-display]");
    this.recoAfterWeightEl = sectionEl.querySelector("[data-ml4448-reco-after-weight]");

    this.resultsEl = sectionEl.querySelector("[data-ml4448-results]");
    this.loadingEl = sectionEl.querySelector("[data-ml4448-loading]");
    this.emptyEl = sectionEl.querySelector("[data-ml4448-empty]");
    this.noMatchesEl = sectionEl.querySelector("[data-ml4448-no-matches]");

    this.paginationEl = sectionEl.querySelector("[data-ml4448-pagination]");
    this.pageStatusEl = sectionEl.querySelector("[data-ml4448-page-status]");
    this.prevBtn = sectionEl.querySelector("[data-ml4448-prev]");
    this.nextBtn = sectionEl.querySelector("[data-ml4448-next]");

    this.grid = sectionEl.querySelector("[data-ml4448-grid]");
    this.items = [];
    this.filtered = [];

    this.totalSectionPages = 1;
    this.loadedSectionPage = 1;
    this.isFetching = false;
    this.fetchQueue = Promise.resolve();
    this.wishlistObserver = null;
    this.wishlistSyncQueued = false;
    this.lastWishlistRenderKey = "";
    this.weightLbExact = null;
    this.weightInputSnapshot = "";

    this._readInitialItems();
    this._syncWeightLbExactFromInput();
    this._bindEvents();
    this._syncToggles();
    this._syncUnitDependentCopy();
    this._setProductLinkTargets(this.section);
    this._setupWishlistSupport();
    this.applyFilters(true);
  }

  ML4448.prototype._readInitialItems = function () {
    var self = this;
    if (!this.grid) return;

    this.totalSectionPages = toNumber(this.grid.getAttribute("data-total-pages")) || 1;
    this.loadedSectionPage = toNumber(this.grid.getAttribute("data-current-page")) || 1;

    var nodes = this.grid.querySelectorAll("[data-ml4448-product]");
    this.items = Array.prototype.slice.call(nodes);
    this.items.forEach(function (itemEl) {
      self._setItemVisible(itemEl, false);
    });
  };

  ML4448.prototype._setItemVisible = function (itemEl, shouldShow) {
    if (!itemEl || !itemEl.classList) return;

    if (shouldShow) {
      itemEl.classList.remove("ml4448__item--parked");
      itemEl.removeAttribute("aria-hidden");
    } else {
      itemEl.classList.add("ml4448__item--parked");
      itemEl.setAttribute("aria-hidden", "true");
    }
  };

  ML4448.prototype._setProductLinkTargets = function (root) {
    var scope = root || this.section;
    if (!scope || !scope.querySelectorAll) return;

    var links = scope.querySelectorAll("[data-ml4448-product-link], [data-ml4448-wishlist-probe] a, [data-ml4448-wishlist-compat] a");
    Array.prototype.forEach.call(links, function (link) {
      if (!link || !link.setAttribute) return;
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");

      if (link.closest && link.closest("[data-ml4448-wishlist-probe], [data-ml4448-wishlist-compat]")) {
        link.setAttribute("tabindex", "-1");
      }
    });
  };

  ML4448.prototype._openCardInNewTab = function (itemEl) {
    if (!itemEl) return;
    var url = itemEl.getAttribute("data-product-url") || itemEl.getAttribute("data-card-product-url");
    if (!url) {
      var link = itemEl.querySelector("[data-ml4448-product-link]");
      if (link) url = link.getAttribute("href");
    }
    if (!url) return;

    var opened = window.open(url, "_blank", "noopener,noreferrer");
    if (opened) opened.opener = null;
  };

  ML4448.prototype._requestWishlistSync = function () {
    var self = this;
    if (this.wishlistSyncQueued) return;
    this.wishlistSyncQueued = true;

    var runner = function () {
      self.wishlistSyncQueued = false;
      self._syncWishlistButtons();
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(runner);
    } else {
      window.setTimeout(runner, 16);
    }
  };

  ML4448.prototype._pokeWishlistMutation = function () {
    if (!this.section || !document.createElement) return;

    var target = this.section.querySelector("[data-ml4448-wishlist-probe]") || this.section;
    if (!target || !target.appendChild) return;

    try {
      var node = document.createElement("span");
      node.setAttribute("data-ml4448-wishlist-poke", "true");
      node.setAttribute("aria-hidden", "true");
      node.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;";
      target.appendChild(node);
      window.setTimeout(function () {
        if (node.parentNode) node.parentNode.removeChild(node);
      }, 0);
    } catch (e) {
      // Ignore mutation-poke failures.
    }
  };

  ML4448.prototype._notifyWishlistApp = function (reason) {
    if (!this.section) return;

    this._pokeWishlistMutation();

    var detail = {
      root: this.section,
      section: this.section,
      sectionId: this.sectionId,
      reason: reason || "ml4448-products-updated"
    };

    [
      "wishlist-by-square:refresh",
      "square-wishlist:refresh",
      "square:wishlist:refresh",
      "SquareWishlist:refresh",
      "vitals:wishlist:refresh",
      "vitals:refresh",
      "shopify:product-cards:updated",
      "vitals:wishlist:render",
      "square-wishlist:render",
      "product-card:loaded",
      "product:card:loaded"
    ].forEach(function (eventName) {
      try {
        document.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
        window.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
      } catch (e) {
        // Ignore unsupported/unused third-party app hooks.
      }
    });

    var calls = [
      [window.WishlistBySquare, "refresh"],
      [window.WishlistBySquare, "render"],
      [window.SquareWishlist, "refresh"],
      [window.SquareWishlist, "render"],
      [window.VitalsWishlist, "refresh"],
      [window.VitalsWishlist, "render"],
      [window.VitalsWishlist, "mount"],
      [window.Vitals && window.Vitals.wishlist, "refresh"],
      [window.Vitals && window.Vitals.wishlist, "render"],
      [window.Vitals && window.Vitals.Wishlist, "refresh"],
      [window.Vitals && window.Vitals.Wishlist, "render"],
      [window.Vitals && window.Vitals.wishlist, "mount"],
      [window.Vitals && window.Vitals.apps && window.Vitals.apps.wishlist, "refresh"],
      [window.Vitals && window.Vitals.apps && window.Vitals.apps.wishlist, "render"],
      [window.Vitals && window.Vitals.apps && window.Vitals.apps.wishlist, "mount"]
    ];

    calls.forEach(function (pair) {
      var obj = pair[0];
      var method = pair[1];
      if (!obj || typeof obj[method] !== "function") return;

      try {
        obj[method](detail.root);
      } catch (e) {
        try {
          obj[method]();
        } catch (ignored) {
          // Ignore unsupported/unused third-party app hooks.
        }
      }
    });
  };

  ML4448.prototype._scheduleWishlistSyncPasses = function (reason) {
    var self = this;

    this._notifyWishlistApp(reason);
    this._requestWishlistSync();

    [150, 400, 900, 1600, 3000, 5000].forEach(function (delay) {
      window.setTimeout(function () {
        self._notifyWishlistApp(reason);
        self._requestWishlistSync();
      }, delay);
    });
  };

  ML4448.prototype._wishlistSelector = function () {
    return [
      ".Vtl-WishlistButtonWrapper",
      ".Vtl-WishlistButton",
      ".Vtl-WishlistButton__AddToWishlist",
      "[class*=\"Vtl-Wishlist\"]",
      ".Vitals-WishlistButtonWrapper",
      ".Vitals-WishlistButton",
      ".SquareWishlistButtonWrapper",
      ".SquareWishlistButton",
      ".square-wishlist-button-wrapper",
      ".square-wishlist-button",
      ".wishlist-button-wrapper",
      ".wishlist-button",
      ".wishlist__button",
      ".WishlistButton",
      ".WishListButton",
      "[data-wishlist]",
      "[data-wishlist-button]",
      "[data-square-wishlist]",
      "[data-square-wishlist-button]",
      "[data-vitals-wishlist]",
      "[data-vitals-wishlist-button]",
      "[data-wishlist-add]",
      "[data-add-to-wishlist]",
      "wishlist-button",
      "square-wishlist-button",
      "vitals-wishlist-button",
      "ap-wishlistbutton",
      "[data-vitals-wishlist]",
      "[data-vitals-wishlist-button]",
      "[class*=\"Wishlist\"]",
      "[class*=\"WishList\"]",
      "[class*=\"wishlist\"]",
      "[class*=\"wish-list\"]",
      "button[aria-label*=\"Wishlist\"]",
      "button[aria-label*=\"wishlist\"]",
      "button[aria-label*=\"Wish\"]",
      "button[aria-label*=\"wish\"]",
      "a[aria-label*=\"Wishlist\"]",
      "a[aria-label*=\"wishlist\"]",
      "a[aria-label*=\"Wish\"]",
      "a[aria-label*=\"wish\"]",
      "button[title*=\"Wishlist\"]",
      "button[title*=\"wishlist\"]",
      "button[title*=\"Wish\"]",
      "button[title*=\"wish\"]",
      "a[title*=\"Wishlist\"]",
      "a[title*=\"wishlist\"]",
      "a[title*=\"Wish\"]",
      "a[title*=\"wish\"]"
    ].join(",");
  };

  ML4448.prototype._isOwnWishlistShell = function (node) {
    if (!node || node.nodeType !== 1) return false;

    if (node.hasAttribute && (
      node.hasAttribute("data-ml4448-wishlist-slot") ||
      node.hasAttribute("data-ml4448-wishlist-app-block") ||
      node.hasAttribute("data-ml4448-wishlist-probe") ||
      node.hasAttribute("data-ml4448-wishlist-compat")
    )) {
      return true;
    }

    var className = typeof node.className === "string" ? node.className : "";
    return className.indexOf("ml4448__wishlist") !== -1;
  };

  ML4448.prototype._wishlistRootForCandidate = function (candidate, itemEl, slot) {
    if (!candidate || !itemEl || !slot) return null;
    if (this._isOwnWishlistShell(candidate)) return null;
    if (candidate === slot || slot.contains(candidate)) return null;

    var wrapperSelectors = [
      ".Vtl-WishlistButtonWrapper",
      ".Vtl-WishlistButton",
      ".Vtl-WishlistButton__AddToWishlist",
      ".Vitals-WishlistButtonWrapper",
      ".SquareWishlistButtonWrapper",
      ".square-wishlist-button-wrapper",
      ".wishlist-button-wrapper",
      "[data-wishlist-wrapper]",
      "[data-square-wishlist-wrapper]",
      "[data-vitals-wishlist-wrapper]",
      "[data-vitals-wishlist-wrapper]"
    ].join(",");

    var wrapper = candidate.closest ? candidate.closest(wrapperSelectors) : null;
    if (wrapper && itemEl.contains(wrapper) && !slot.contains(wrapper)) return wrapper;

    var interactive = candidate.closest ? candidate.closest("button,a,[role='button']") : null;
    if (interactive && itemEl.contains(interactive) && !slot.contains(interactive)) return interactive;

    return itemEl.contains(candidate) ? candidate : null;
  };

  ML4448.prototype._slotContainsWishlistButton = function (slot) {
    var self = this;
    if (!slot) return false;

    var selector = this._wishlistSelector();
    var found = Array.prototype.slice.call(slot.querySelectorAll(selector));
    return found.some(function (node) {
      return node !== slot && !self._isOwnWishlistShell(node);
    });
  };

  ML4448.prototype._syncWishlistButtons = function () {
    var self = this;
    if (!this.items || !this.items.length) return;

    var selector = this._wishlistSelector();

    this.items.forEach(function (itemEl) {
      if (!itemEl) return;

      var slot = itemEl.querySelector("[data-ml4448-wishlist-slot]");
      if (!slot) return;

      if (self._slotContainsWishlistButton(slot)) {
        var duplicates = Array.prototype.slice.call(itemEl.querySelectorAll(selector));
        duplicates.forEach(function (candidate) {
          if (!candidate || self._isOwnWishlistShell(candidate) || slot.contains(candidate)) return;
          var duplicateRoot = self._wishlistRootForCandidate(candidate, itemEl, slot);
          if (duplicateRoot && duplicateRoot.parentNode && !slot.contains(duplicateRoot)) {
            duplicateRoot.parentNode.removeChild(duplicateRoot);
          }
        });
        slot.setAttribute("data-ml4448-wishlist-mounted", "true");
        return;
      }

      var candidates = Array.prototype.slice.call(itemEl.querySelectorAll(selector));
      for (var i = 0; i < candidates.length; i++) {
        if (self._isOwnWishlistShell(candidates[i])) continue;
        var root = self._wishlistRootForCandidate(candidates[i], itemEl, slot);
        if (!root) continue;

        slot.appendChild(root);
        slot.setAttribute("data-ml4448-wishlist-mounted", "true");
        break;
      }
    });
  };

  ML4448.prototype._setupWishlistSupport = function () {
    var self = this;
    if (!this.section) return;
    if (this.wishlistObserver) return;

    if (typeof MutationObserver === "function") {
      this.wishlistObserver = new MutationObserver(function (mutations) {
        var shouldSync = false;
        var shouldNotify = false;

        for (var i = 0; i < mutations.length; i++) {
          var mutation = mutations[i];
          if (!mutation || !mutation.addedNodes || !mutation.addedNodes.length) continue;

          shouldSync = true;

          for (var n = 0; n < mutation.addedNodes.length; n++) {
            var added = mutation.addedNodes[n];
            if (added && added.nodeType === 1 && added.hasAttribute && added.hasAttribute("data-ml4448-product")) {
              shouldNotify = true;
              break;
            }
          }

          if (shouldNotify) break;
        }

        if (shouldNotify) self._notifyWishlistApp("ml4448-products-added");
        if (shouldSync) self._requestWishlistSync();
      });

      this.wishlistObserver.observe(this.section, { childList: true, subtree: true });
    }

    this._scheduleWishlistSyncPasses("ml4448-initialized");
  };

  ML4448.prototype._bindEvents = function () {
    var self = this;

    this.subjectBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var next = btn.getAttribute("data-ml4448-subject");
        if (!next || next === self.subject) return;
        self.subject = next;
        self._setSubjectError(false);
        self.currentPage = 1;
        self._syncToggles();
        self.applyFilters(true);
      });
    });

    this.unitBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var next = btn.getAttribute("data-ml4448-unit");
        if (!next || next === self.unit) return;

        var canonicalLb = self._getWeightLbExact();

        self.unit = next;

        if (Number.isFinite(canonicalLb) && canonicalLb > 0) {
          self._setDisplayedWeightFromLb(canonicalLb);
        } else {
          self._syncWeightLbExactFromInput();
        }

        self._syncToggles();
        self._syncUnitDependentCopy();
        self.applyFilters(false);
      });
    });

    if (this.weightInput) {
      this.weightInput.addEventListener(
        "input",
        debounce(function () {
          self._syncWeightLbExactFromInput();
          self.currentPage = 1;
          self.applyFilters(true);
        }, 200)
      );
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", function () {
        if (self.prevBtn.disabled) return;
        self.goToPage(self.currentPage - 1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", function () {
        if (self.nextBtn.disabled) return;
        self.goToPage(self.currentPage + 1);
      });
    }

    if (this.grid) {
      this.grid.addEventListener("click", function (event) {
        if (!event || !event.target) return;

        if (event.target.closest && event.target.closest("[data-ml4448-wishlist-slot], [data-ml4448-wishlist-probe]")) return;

        var interactive = event.target.closest
          ? event.target.closest("a, button, input, select, textarea, summary, [role='button'], [tabindex]:not([tabindex='-1'])")
          : null;
        if (interactive) return;

        var itemEl = event.target.closest ? event.target.closest("[data-ml4448-product]") : null;
        if (!itemEl || !self.grid.contains(itemEl)) return;
        if (itemEl.classList && itemEl.classList.contains("ml4448__item--parked")) return;

        self._openCardInNewTab(itemEl);
      });
    }
  };

  ML4448.prototype._syncToggles = function () {
    var self = this;

    this.subjectBtns.forEach(function (btn) {
      var val = btn.getAttribute("data-ml4448-subject");
      btn.setAttribute("aria-pressed", val === self.subject ? "true" : "false");
    });

    this.unitBtns.forEach(function (btn) {
      var val = btn.getAttribute("data-ml4448-unit");
      btn.setAttribute("aria-pressed", val === self.unit ? "true" : "false");
    });
  };

  ML4448.prototype._syncWeightLbExactFromInput = function () {
    if (!this.weightInput) {
      this.weightLbExact = null;
      this.weightInputSnapshot = "";
      return null;
    }

    var raw = String(this.weightInput.value || "");
    this.weightInputSnapshot = raw;

    var w = toNumber(raw);
    if (!Number.isFinite(w) || w <= 0) {
      this.weightLbExact = null;
      return null;
    }

    this.weightLbExact = this.unit === "kg" ? w * LB_PER_KG : w;
    return this.weightLbExact;
  };

  ML4448.prototype._getWeightLbExact = function () {
    if (!this.weightInput) return null;

    var raw = String(this.weightInput.value || "");
    if (raw !== this.weightInputSnapshot) {
      return this._syncWeightLbExactFromInput();
    }

    if (!Number.isFinite(this.weightLbExact) || this.weightLbExact <= 0) {
      return this._syncWeightLbExactFromInput();
    }

    return this.weightLbExact;
  };

  ML4448.prototype._setDisplayedWeightFromLb = function (lbExact) {
    if (!this.weightInput) return;

    if (!Number.isFinite(lbExact) || lbExact <= 0) {
      this.weightInput.value = "";
      this.weightInputSnapshot = "";
      return;
    }

    var displayed = this.unit === "kg" ? lbExact / LB_PER_KG : lbExact;
    var nextValue = formatSmart(displayed);
    this.weightInput.value = nextValue;
    this.weightInputSnapshot = nextValue;
  };

  ML4448.prototype._formatWeightForCurrentUnit = function (lbValue) {
    if (!Number.isFinite(lbValue)) return "—";
    var displayed = this.unit === "kg" ? lbValue / LB_PER_KG : lbValue;
    return formatSmart(displayed);
  };

  ML4448.prototype._replaceUnitTemplate = function (template) {
    if (template === null || template === undefined) return "";
    var text = String(template);
    var unitLabel = this._currentUnitLabel();
    return text.replace(/\b(?:lb|lbs|pound|pounds|kg|kgs|kilogram|kilograms)\b/i, unitLabel);
  };

  ML4448.prototype._syncUnitDependentCopy = function () {
    var self = this;

    if (this.recoAfterWeightEl) {
      var recoTemplate = this.recoAfterWeightEl.getAttribute("data-ml4448-template");
      if (!recoTemplate) recoTemplate = this.recoAfterWeightEl.textContent || "";
      this.recoAfterWeightEl.textContent = this._replaceUnitTemplate(recoTemplate);
    }

    this.items.forEach(function (itemEl) {
      if (!itemEl) return;

      var cap = toNumber(itemEl.getAttribute("data-capacity"));
      var capValueEl = itemEl.querySelector("[data-ml4448-card-capacity]");
      if (capValueEl) {
        capValueEl.textContent = Number.isFinite(cap) ? self._formatWeightForCurrentUnit(cap) : "—";
      }

      var capUnitEl = itemEl.querySelector("[data-ml4448-card-capacity-unit]");
      if (capUnitEl) {
        var capUnitTemplate = capUnitEl.getAttribute("data-ml4448-template");
        if (!capUnitTemplate) capUnitTemplate = capUnitEl.textContent || "";
        capUnitEl.textContent = self._replaceUnitTemplate(capUnitTemplate);
      }
    });
  };

  ML4448.prototype._requiredCapacityLb = function () {
    var lb = this._getWeightLbExact();
    if (!Number.isFinite(lb) || lb <= 0) return null;

    // Round up to avoid under-sizing when CAP tags are whole numbers.
    return Math.ceil(lb);
  };

  ML4448.prototype._enteredWeightLbExact = function () {
    var lb = this._getWeightLbExact();
    if (!Number.isFinite(lb) || lb <= 0) return null;
    return lb;
  };

  ML4448.prototype._enteredWeightRaw = function () {
    if (!this.weightInput) return null;
    var w = toNumber(this.weightInput.value);
    if (!Number.isFinite(w) || w <= 0) return null;
    return w;
  };

  ML4448.prototype._currentUnitLabel = function () {
    var btn = this.section.querySelector('[data-ml4448-unit="' + this.unit + '"]');
    if (!btn) return this.unit;
    return (btn.textContent || this.unit).trim();
  };

  ML4448.prototype._currentSubjectLabel = function () {
    var btn = this.section.querySelector('[data-ml4448-subject="' + this.subject + '"]');
    if (!btn) return this.subject === "pet" ? "Pet" : "Loved one";
    return (btn.textContent || (this.subject === "pet" ? "Pet" : "Loved one")).trim();
  };

  ML4448.prototype._matches = function (itemEl, requiredLb) {
    if (!requiredLb) return false;
    if (!itemEl) return false;

    var type = itemEl.getAttribute("data-urn-type") || "";
    if (type !== this.subject) return false;

    var cap = toNumber(itemEl.getAttribute("data-capacity"));
    if (!Number.isFinite(cap)) return false;

    return cap >= requiredLb;
  };

  ML4448.prototype._setLoading = function (isLoading) {
    if (!this.loadingEl) return;
    if (isLoading) {
      this.loadingEl.classList.add("is-visible");
      this.loadingEl.setAttribute("aria-hidden", "false");
    } else {
      this.loadingEl.classList.remove("is-visible");
      this.loadingEl.setAttribute("aria-hidden", "true");
    }
  };

  ML4448.prototype._setSubjectError = function (show) {
    if (this.subjectErrorEl) this.subjectErrorEl.hidden = !show;

    if (this.subjectSegEl) {
      if (show) this.subjectSegEl.classList.add("ml4448__seg--error");
      else this.subjectSegEl.classList.remove("ml4448__seg--error");
    }
  };

  ML4448.prototype._updatePageStatus = function (totalPages) {
    if (!this.pageStatusEl) return;
    this.pageStatusEl.textContent = "Page " + this.currentPage + " of " + totalPages;
  };

  ML4448.prototype._scrollToFirstVisibleCard = function () {
    if (!this.items || !this.items.length) return;

    var firstVisible = null;
    for (var i = 0; i < this.items.length; i++) {
      var itemEl = this.items[i];
      if (itemEl && itemEl.classList && !itemEl.classList.contains("ml4448__item--parked")) {
        firstVisible = itemEl.querySelector(".ml4448__card") || itemEl;
        break;
      }
    }

    if (!firstVisible || typeof firstVisible.scrollIntoView !== "function") return;

    var behavior = preferredScrollBehavior();
    window.requestAnimationFrame(function () {
      firstVisible.scrollIntoView({ behavior: behavior, block: "start" });
    });
  };

  ML4448.prototype.applyFilters = function (ensureFirstPage) {
    var self = this;
    var requiredLb = this._requiredCapacityLb();
    var hasWeight = !!requiredLb;
    var hasSubject = this.subject === "human" || this.subject === "pet";

    // When no weight has been provided, hide results and validation messaging.
    if (!hasWeight) {
      this._setSubjectError(false);
      this.filtered = [];
      this._render();
      return;
    }

    // Require the user to choose Loved one / Pet before showing results.
    if (!hasSubject) {
      this._setSubjectError(true);
      this.filtered = [];
      this._render();
      return;
    }

    this._setSubjectError(false);


    var targetPage = ensureFirstPage ? 1 : this.currentPage;
    this.currentPage = targetPage;

    // Load the full product set once so the displayed total count is accurate.
    this.fetchQueue = this.fetchQueue
      .then(function () {
        return self._ensureAllSectionPagesLoaded();
      })
      .catch(function () {
        // On fetch errors, continue with whatever is already loaded.
      })
      .then(function () {
        self.filtered = self.items.filter(function (el) {
          return self._matches(el, requiredLb);
        });
        self._render(requiredLb);
      });
  };

  ML4448.prototype.goToPage = function (page) {
    var self = this;
    var requiredLb = this._requiredCapacityLb();
    if (!requiredLb) return;
    if (!(this.subject === "human" || this.subject === "pet")) return;

    this.currentPage = page;

    this.fetchQueue = this.fetchQueue
      .then(function () {
        return self._ensureMatchesForPage(self.currentPage, requiredLb);
      })
      .then(function () {
        self._render(requiredLb);
      })
      .catch(function () {
        self._render(requiredLb);
      })
      .then(function () {
        self._scrollToFirstVisibleCard();
      });
  };

  ML4448.prototype._ensureMatchesForPage = function (page, requiredLb) {
    var self = this;
    if (!requiredLb) return Promise.resolve();
    if (!this.grid) return Promise.resolve();
    if (this.loadedSectionPage >= this.totalSectionPages) return Promise.resolve();

    var desiredCount = Math.max(1, page) * this.perPage;

    // If we already have enough matches, don't fetch.
    if (this.filtered.length >= desiredCount) return Promise.resolve();

    // Fetch additional section pages until we have enough matches (or run out).
    var loop = function () {
      if (self.filtered.length >= desiredCount) return Promise.resolve();
      if (self.loadedSectionPage >= self.totalSectionPages) return Promise.resolve();
      return self._loadNextSectionPage().then(function () {
        self.filtered = self.items.filter(function (el) {
          return self._matches(el, requiredLb);
        });
        return loop();
      });
    };

    return loop();
  };

  ML4448.prototype._ensureAllSectionPagesLoaded = function () {
    var self = this;
    if (!this.grid) return Promise.resolve();
    if (this.loadedSectionPage >= this.totalSectionPages) return Promise.resolve();

    var loop = function () {
      if (self.loadedSectionPage >= self.totalSectionPages) return Promise.resolve();
      return self._loadNextSectionPage().then(function () {
        return loop();
      });
    };

    return loop();
  };

  ML4448.prototype._loadNextSectionPage = function () {
    var self = this;
    if (this.isFetching) return Promise.resolve();
    if (!this.sectionId) return Promise.resolve();
    if (this.loadedSectionPage >= this.totalSectionPages) return Promise.resolve();

    this.isFetching = true;
    this._setLoading(true);

    var next = this.loadedSectionPage + 1;
    var url = new URL(window.location.href);
    url.searchParams.set("section_id", this.sectionId);
    url.searchParams.set("page", String(next));

    return fetch(url.toString(), { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("ML-4448 fetch failed");
        return res.text();
      })
      .then(function (htmlText) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(htmlText, "text/html");
        var incomingSection = doc.querySelector('[data-ml4448-section-id="' + self.sectionId + '"]');
        var incomingGrid = incomingSection ? incomingSection.querySelector("[data-ml4448-grid]") : doc.querySelector("[data-ml4448-grid]");
        if (!incomingGrid) {
          // Still advance the loaded page to prevent infinite fetch loops.
          self.loadedSectionPage = next;
          return;
        }

        var incomingItems = Array.prototype.slice.call(incomingGrid.querySelectorAll("[data-ml4448-product]"));
        if (!incomingItems.length) {
          self.loadedSectionPage = next;
          return;
        }

        incomingItems.forEach(function (node) {
          // Import into current document.
          var imported = document.importNode(node, true);
          self._setItemVisible(imported, false);
          self._setProductLinkTargets(imported);
          self.grid.appendChild(imported);
          self.items.push(imported);
        });

        self.loadedSectionPage = next;
        self._scheduleWishlistSyncPasses("ml4448-products-loaded");
      })
      .finally(function () {
        self.isFetching = false;
        self._setLoading(false);
      });
  };

  ML4448.prototype._notifyWishlistForCurrentView = function (reason) {
    var ids = [];
    if (this.items && this.items.length) {
      this.items.forEach(function (itemEl) {
        if (!itemEl || !itemEl.classList || itemEl.classList.contains("ml4448__item--parked")) return;
        ids.push(itemEl.getAttribute("data-product-id") || itemEl.getAttribute("data-card-product-id") || itemEl.getAttribute("data-product-handle") || "");
      });
    }

    var key = [reason || "view", this.currentPage, this.filtered ? this.filtered.length : 0, ids.join(",")].join("|");
    if (key === this.lastWishlistRenderKey) {
      this._requestWishlistSync();
      return;
    }

    this.lastWishlistRenderKey = key;
    this._scheduleWishlistSyncPasses(reason || "ml4448-products-rendered");
  };

  ML4448.prototype._render = function (requiredLb) {
    var self = this;
    var hasWeight = !!requiredLb;
    var hasSubject = this.subject === "human" || this.subject === "pet";
    var isReady = hasWeight && hasSubject;

    if (this.recoEl) this.recoEl.hidden = !isReady;
    if (this.resultsEl) {
      this.resultsEl.classList.toggle("is-ready", isReady);
    }

    this._syncUnitDependentCopy();

    // Update the recommendation text.
    if (isReady) {
      var lbExact = this._enteredWeightLbExact();
      var enteredWeight = this._enteredWeightRaw();
      var weightDisplay = this._formatWeightForCurrentUnit(lbExact);
      var capacityDisplay = formatSmart(lbExact);
      var enteredWeightDisplay = formatSmart(enteredWeight);
      var unitLabel = this._currentUnitLabel();
      var subjectLabel = this._currentSubjectLabel();

      if (this.weightDisplayEl) this.weightDisplayEl.textContent = weightDisplay;
      if (this.capacityDisplayEl) this.capacityDisplayEl.textContent = capacityDisplay;
      if (this.totalCountEl) this.totalCountEl.textContent = String(this.filtered.length);
      if (this.enteredWeightUnitEl) this.enteredWeightUnitEl.textContent = enteredWeightDisplay + " " + unitLabel;
      if (this.subjectDisplayEl) this.subjectDisplayEl.textContent = subjectLabel;
    } else {
      if (this.weightDisplayEl) this.weightDisplayEl.textContent = "—";
      if (this.capacityDisplayEl) this.capacityDisplayEl.textContent = "—";
      if (this.totalCountEl) this.totalCountEl.textContent = "0";
      if (this.enteredWeightUnitEl) this.enteredWeightUnitEl.textContent = "—";
      if (this.subjectDisplayEl) this.subjectDisplayEl.textContent = "—";
    }

    // Park all items unless we have both a valid weight and a subject selection.
    if (!isReady) {
      this.items.forEach(function (el) {
        self._setItemVisible(el, false);
      });
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.noMatchesEl) this.noMatchesEl.hidden = true;
      if (this.paginationEl) this.paginationEl.hidden = true;
      if (this.pageStatusEl) this.pageStatusEl.textContent = "";
      this.lastWishlistRenderKey = "";
      this._requestWishlistSync();
      return;
    }

    var hasAnyProducts = this.items.length > 0;
    var hasMatches = this.filtered.length > 0;

    if (this.emptyEl) this.emptyEl.hidden = hasAnyProducts;
    if (this.noMatchesEl) this.noMatchesEl.hidden = !hasAnyProducts || hasMatches;

    var totalPages = Math.max(1, Math.ceil(this.filtered.length / this.perPage));
    this.currentPage = clamp(this.currentPage, 1, totalPages);

    var start = (this.currentPage - 1) * this.perPage;
    var end = start + this.perPage;
    var visible = this.filtered.slice(start, end);
    var visibleSet = new Set(visible);

    this.items.forEach(function (el) {
      self._setItemVisible(el, visibleSet.has(el));
    });

    // Pagination controls.
    if (this.paginationEl) {
      this.paginationEl.hidden = this.filtered.length <= this.perPage || !hasMatches;
    }

    this._updatePageStatus(totalPages);

    if (this.prevBtn) this.prevBtn.disabled = this.currentPage <= 1;
    if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= totalPages;

    this._notifyWishlistForCurrentView("ml4448-products-rendered");
  };

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll("[data-ml4448-section]");
    Array.prototype.forEach.call(sections, function (sectionEl) {
      if (sectionEl.hasAttribute("data-ml4448-initialized")) return;
      sectionEl.setAttribute("data-ml4448-initialized", "true");
      try {
        new ML4448(sectionEl);
      } catch (e) {
        // Fail silently to avoid breaking the page.
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (!event || !event.target) return;
    initAll(event.target);
  });
})();
